import React, { Fragment, useEffect, useState } from "react";
import { Modal, Button, Table, Form, Badge } from "react-bootstrap";
import { collection, getDocs, orderBy, query } from "firebase/firestore";
import { db } from "../../../../firebase";

const dateInputValue = (value) => {
  if (!value) return "";
  const date = value.toDate ? value.toDate() : new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toISOString().slice(0, 10);
};

const initialRow = (item) => {
  const legacyPositive =
    item.lotTrackingVersion !== 1 && Number(item.stockQuantity) > 0;
  return {
    quantity: "",
    mode: legacyPositive ? "opening" : "new",
    lotId: "",
    lotNo: "",
    incomingDate: "",
    expiryDate: "",
    openingLotNo: item.lotNo || "",
    openingIncomingDate: dateInputValue(item.incomingDate),
    openingExpiryDate: dateInputValue(item.expiryDate),
  };
};

const BulkStockUpdateModal = ({ show, onHide, selectedItems, accessories, onSubmit }) => {
  const [rows, setRows] = useState({});
  const [lotsByItem, setLotsByItem] = useState({});
  const [notes, setNotes] = useState("");
  const [loadingLots, setLoadingLots] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const selectedAccessories = accessories.filter((item) =>
    selectedItems.includes(item.id)
  );
  const hasLegacyItems = selectedAccessories.some(
    (item) => item.lotTrackingVersion !== 1
  );

  useEffect(() => {
    if (!show) return undefined;
    const selected = accessories.filter((item) => selectedItems.includes(item.id));
    setRows(Object.fromEntries(selected.map((item) => [item.id, initialRow(item)])));
    setLotsByItem({});
    setNotes("");
    setSubmitting(false);
    setLoadingLots(true);
    let active = true;
    Promise.all(selectedItems.map(async (id) => {
      const snapshot = await getDocs(query(
        collection(db, "accessories", id, "lots"),
        orderBy("incomingDate", "asc")
      ));
      return [id, snapshot.docs.map((lotDoc) => ({ id: lotDoc.id, ...lotDoc.data() }))];
    })).then((entries) => {
      if (active) setLotsByItem(Object.fromEntries(entries));
    }).catch((error) => {
      console.error("Error loading lots:", error);
    }).finally(() => {
      if (active) setLoadingLots(false);
    });
    return () => { active = false; };
  }, [show, selectedItems, accessories]);

  const updateRow = (id, changes) => setRows((current) => ({
    ...current,
    [id]: { ...current[id], ...changes },
  }));

  const chooseExistingLot = (id, lotId) => {
    const lot = (lotsByItem[id] || []).find((entry) => entry.id === lotId);
    updateRow(id, lot ? {
      lotId,
      lotNo: lot.lotNo,
      incomingDate: dateInputValue(lot.incomingDate),
      expiryDate: dateInputValue(lot.expiryDate),
    } : { lotId: "", lotNo: "", incomingDate: "", expiryDate: "" });
  };

  const changeMode = (id, mode) => {
    const quantity = rows[id] ? rows[id].quantity : "";
    updateRow(id, {
      quantity,
      mode,
      lotId: "",
      lotNo: "",
      incomingDate: "",
      expiryDate: "",
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const updates = [];
    for (const item of selectedAccessories) {
      const row = rows[item.id] || initialRow(item);
      const legacy = item.lotTrackingVersion !== 1;
      const currentStock = Number(item.stockQuantity) || 0;
      const legacyPositive = legacy && currentStock > 0;
      const quantityText = String(row.quantity || "").trim();
      const amountAdded = quantityText === "" ? 0 : Number(quantityText);

      if (legacyPositive && (!String(row.openingLotNo).trim() || !row.openingIncomingDate)) {
        alert(`Opening Lot No. and incoming date are required for ${item.itemName}.`);
        return;
      }
      if ((!legacyPositive && amountAdded <= 0) ||
          (quantityText !== "" && (!Number.isInteger(amountAdded) || amountAdded <= 0))) {
        alert(`Enter a positive whole-number receipt quantity for ${item.itemName}.`);
        return;
      }
      if (amountAdded > 0 && row.mode === "existing" && !row.lotId) {
        alert(`Select an existing lot for ${item.itemName}.`);
        return;
      }
      if (amountAdded > 0 && row.mode === "new" &&
          (!String(row.lotNo).trim() || !row.incomingDate)) {
        alert(`Receipt Lot No. and incoming date are required for ${item.itemName}.`);
        return;
      }

      updates.push({
        accessoryId: item.id,
        expectedStock: currentStock,
        openingLot: legacyPositive ? {
          lotNo: String(row.openingLotNo).trim(),
          incomingDate: row.openingIncomingDate,
          expiryDate: row.openingExpiryDate || null,
        } : null,
        receipt: amountAdded > 0 ? {
          quantity: amountAdded,
          mode: row.mode,
          lotId: row.mode === "existing" ? row.lotId : null,
          lotNo: row.mode === "opening" ? row.openingLotNo : row.lotNo,
          incomingDate: row.mode === "opening"
            ? row.openingIncomingDate
            : row.incomingDate,
          expiryDate: row.mode === "opening"
            ? row.openingExpiryDate || null
            : row.expiryDate || null,
          notes: notes || `Added ${amountAdded} units`,
        } : null,
      });
    }

    setSubmitting(true);
    try {
      await onSubmit(updates);
    } finally {
      setSubmitting(false);
    }
  };

  const renderReceiptFields = (item, row) => {
    const legacyPositive =
      item.lotTrackingVersion !== 1 && Number(item.stockQuantity) > 0;
    const usesOpeningLot = legacyPositive && row.mode === "opening";
    const readOnly = usesOpeningLot || row.mode === "existing";
    const lotNo = usesOpeningLot ? row.openingLotNo : row.lotNo;
    const incomingDate = usesOpeningLot
      ? row.openingIncomingDate
      : row.incomingDate;
    const expiryDate = usesOpeningLot
      ? row.openingExpiryDate
      : row.expiryDate;
    return (
      <>
        <td>
          <Form.Control
            readOnly={readOnly}
            size="sm"
            value={lotNo || ""}
            onChange={(event) => updateRow(item.id, { lotNo: event.target.value })}
          />
        </td>
        <td>
          <Form.Control
            readOnly={readOnly}
            type="date"
            size="sm"
            value={incomingDate || ""}
            onChange={(event) => updateRow(item.id, { incomingDate: event.target.value })}
          />
        </td>
        <td>
          <Form.Control
            readOnly={readOnly}
            type="date"
            size="sm"
            value={expiryDate || ""}
            onChange={(event) => updateRow(item.id, { expiryDate: event.target.value })}
          />
        </td>
      </>
    );
  };

  return (
    <Modal show={show} onHide={submitting ? undefined : onHide} size="xl" backdrop="static">
      <Modal.Header><Modal.Title>Add Stock - {selectedItems.length} Items</Modal.Title></Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit}>
          {hasLegacyItems && (
            <div className="alert alert-info">
              Legacy accessories with stock use the blue opening-balance row to map current stock to one lot without changing the stock total or creating a Stock Log. A zero-stock legacy accessory starts tracking with its first receipt.
            </div>
          )}
          <Form.Group>
            <Form.Label>Notes <small className="text-muted">(Optional, used only for received stock)</small></Form.Label>
            <Form.Control as="textarea" rows="2" value={notes} onChange={(event) => setNotes(event.target.value)} />
          </Form.Group>
          <Table striped bordered size="sm" responsive>
            <thead>
              <tr>
                <th>Item</th>
                <th>Current</th>
                <th style={{ width: "80px" }}>Quantity</th>
                <th style={{ minWidth: "220px" }}>Lot choice</th>
                <th>Lot No.</th>
                <th>Incoming</th>
                <th>Expiry</th>
                <th>New total</th>
              </tr>
            </thead>
            <tbody>
              {selectedAccessories.map((item) => {
                const row = rows[item.id] || initialRow(item);
                const lots = lotsByItem[item.id] || [];
                const currentStock = Number(item.stockQuantity) || 0;
                const legacy = item.lotTrackingVersion !== 1;
                const legacyPositive = legacy && currentStock > 0;
                const amountAdded = parseInt(row.quantity, 10) || 0;
                return (
                  <Fragment key={item.id}>
                    {legacyPositive && (
                      <tr className="table-info">
                        <td rowSpan="2">{item.itemCode}<br /><small>{item.itemName}</small></td>
                        <td rowSpan="2">{currentStock}</td>
                        <td><small>Existing: {currentStock}</small></td>
                        <td><Badge variant="info">Opening balance</Badge></td>
                        <td>
                          <Form.Control required size="sm" value={row.openingLotNo || ""} onChange={(event) => updateRow(item.id, { openingLotNo: event.target.value })} />
                        </td>
                        <td>
                          <Form.Control required type="date" size="sm" value={row.openingIncomingDate || ""} onChange={(event) => updateRow(item.id, { openingIncomingDate: event.target.value })} />
                        </td>
                        <td>
                          <Form.Control type="date" size="sm" value={row.openingExpiryDate || ""} onChange={(event) => updateRow(item.id, { openingExpiryDate: event.target.value })} />
                        </td>
                        <td>{currentStock}</td>
                      </tr>
                    )}
                    <tr>
                      {!legacyPositive && (
                        <>
                          <td>{item.itemCode}<br /><small>{item.itemName}</small></td>
                          <td>{currentStock}</td>
                        </>
                      )}
                      <td style={{ width: "80px" }}>
                        <Form.Control
                          required={!legacyPositive}
                          type="number"
                          min="1"
                          step="1"
                          size="sm"
                          value={row.quantity || ""}
                          onChange={(event) => updateRow(item.id, { quantity: event.target.value })}
                          placeholder={legacyPositive ? "Optional" : "Qty"}
                          style={{ width: "80px" }}
                        />
                      </td>
                      <td style={{ minWidth: "220px" }}>
                        {legacyPositive ? (
                          <Form.Control as="select" size="sm" value={row.mode} onChange={(event) => changeMode(item.id, event.target.value)}>
                            <option value="opening">Same as opening lot</option>
                            <option value="new">New lot</option>
                          </Form.Control>
                        ) : legacy ? (
                          <Badge variant="primary">First lot</Badge>
                        ) : (
                          <>
                            <Form.Control as="select" size="sm" value={row.mode} onChange={(event) => changeMode(item.id, event.target.value)}>
                              <option value="new">New lot</option>
                              <option value="existing">Existing lot</option>
                            </Form.Control>
                            {row.mode === "existing" && (
                              <Form.Control className="mt-1" as="select" required size="sm" value={row.lotId} onChange={(event) => chooseExistingLot(item.id, event.target.value)}>
                                <option value="">Select lot...</option>
                                {lots.map((lot) => (
                                  <option key={lot.id} value={lot.id}>
                                    {lot.lotNo} ({lot.stockQuantity || 0})
                                  </option>
                                ))}
                              </Form.Control>
                            )}
                          </>
                        )}
                      </td>
                      {renderReceiptFields(item, row)}
                      <td>{currentStock + amountAdded}</td>
                    </tr>
                  </Fragment>
                );
              })}
            </tbody>
          </Table>
          <div className="d-flex justify-content-end">
            <Button variant="secondary" className="mr-2" onClick={onHide} disabled={submitting}>Cancel</Button>
            <Button variant="primary" type="submit" disabled={loadingLots || submitting}>
              {submitting ? "Saving..." : "Save Stock"}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default BulkStockUpdateModal;
